import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ParticipantFace, CARD_PICTURE_NAMES } from '../faces/ParticipantFace';
import { BoxPicture } from '../BoxPicture/BoxPicture';
import { FileUpload, type FileUploadHandle } from '../FileUpload/FileUpload';
import { Hint } from '../Hint/Hint';
import { PicturePicker } from './PicturePicker';

const CUSTOM_AVATAR = 'custom_avatar';
const pictures = [...CARD_PICTURE_NAMES, CUSTOM_AVATAR];

export function ParticipantPicturePicker({
  value,
  avatar,
  disabled,
  onValueChange,
  onUploadingChange,
  testId,
}: {
  value: string;
  avatar?: string | null;
  disabled?: boolean;
  onValueChange: (picture: string, avatar: string) => void;
  onUploadingChange?: (uploading: boolean) => void;
  testId: string;
}) {
  const { t } = useTranslation();
  const upload = useRef<FileUploadHandle>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <PicturePicker
        value={value}
        pictures={pictures}
        disabled={disabled || uploading}
        loadingPicture={uploading ? CUSTOM_AVATAR : null}
        renderPicture={(name) =>
          name === CUSTOM_AVATAR && !avatar ? (
            <BoxPicture picture="custom_image" />
          ) : (
            <ParticipantFace picture={name} avatar={avatar} />
          )
        }
        onValueChange={(name) => {
          setError(null);
          if (name === CUSTOM_AVATAR) upload.current?.open();
          else onValueChange(name, '');
        }}
        testId={testId}
      />
      {error && <Hint>{error}</Hint>}
      <FileUpload
        ref={upload}
        kind="image"
        onChange={(state, file) => {
          const pending = state === 'selected';
          setUploading(pending);
          onUploadingChange?.(pending);
          if (state === 'selected') setError(null);
          if (state === 'uploaded' && file) onValueChange(CUSTOM_AVATAR, file.url);
          if (state === 'error') setError(t('upload.failed'));
        }}
      />
    </>
  );
}
